import * as React from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUIStore, type ColorMode } from '@/stores/uiStore';

const cycleMode = (mode: ColorMode): ColorMode => {
  if (mode === 'light') return 'dark';
  if (mode === 'dark') return 'system';
  return 'light';
};

const modeLabel: Record<ColorMode, string> = {
  light: 'Light mode',
  dark: 'Dark mode',
  system: 'System theme',
};

export interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className, size = 'sm' }) => {
  const colorMode = useUIStore((state) => state.colorMode);
  const setColorMode = useUIStore((state) => state.setColorMode);
  const resolvedTheme = useUIStore((state) => state.resolvedTheme);

  const next = cycleMode(colorMode);

  return (
    <button
      type="button"
      onClick={() => setColorMode(next)}
      className={cn(
        'inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-control border border-hairline bg-paper font-ui text-content-secondary transition-[color,background-color,border-color] duration-200 ease-out hover:border-ink hover:bg-stone hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-parchment',
        size === 'sm' && 'size-11',
        className
      )}
      aria-label={`Theme: ${modeLabel[colorMode]}. Switch to ${modeLabel[next]}.`}
      title={`${modeLabel[colorMode]} · click for ${modeLabel[next]}`}
    >
      {resolvedTheme === 'dark' ? (
        <Moon className="size-4" aria-hidden="true" />
      ) : (
        <Sun className="size-4" aria-hidden="true" />
      )}
    </button>
  );
};
